import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
  StyleSheet,
  Share,
  Text,
  TextInput,
  TouchableOpacity,
  View,
  FlatList,
  Linking,
  Platform,
  Keyboard,
  ListRenderItem,
  NativeSyntheticEvent,
  TextInputChangeEventData,
  useWindowDimensions,
} from 'react-native';
import { unionBy } from 'lodash';
import SocialShare, { Social, ShareSingleOptions } from 'react-native-share';
import { useQuery } from 'react-apollo';
import Clipboard from '@react-native-clipboard/clipboard';
import { User } from '../../common-types/types';
import { Colors, Typography } from '../../common-styles';
import { Avatar, LoadingIndicator, MeshIcon, ThemedButton } from '../../common-ui';
import { client, FOLLOW_USER, SEARCH_PERSONAS, UNFOLLOW_USER } from '../../graphql';
import { Events, PendoTrackEvent } from '../../pendo/events';

// @ts-ignore reason: svg import type definition does not exist
import ShareViaIcon from '../../../assets/images/ShareVia.svg';
// @ts-ignore
import CopyLinkIcon from '../../../assets/images/CopyLink.svg';
// @ts-ignore
import MessageIcon from '../../../assets/images/Messages.svg';
// @ts-ignore
import GoogleMessageIcon from '../../../assets/images/GoogleMessage.svg';
// @ts-ignore
import WhatsAppIcon from '../../../assets/images/WhatsApp.svg';
// @ts-ignore
import EmailIcon from '../../../assets/images/Email.svg';
// @ts-ignore
import MessengerIcon from '../../../assets/images/Messenger.svg';
// @ts-ignore
import InstagramIcon from '../../../assets/images/Instagram.svg';
// @ts-ignore
import TelegramIcon from '../../../assets/images/Telegram.svg';
// @ts-ignore
import TwitterIcon from '../../../assets/images/Twitter.svg';
import { setPersonalFeedRefresh } from '../../redux/feedSlice';

import { MeshVerifiedCheck } from '../../images';
import { useNavigation } from '@react-navigation/native';
import { useDispatch } from 'react-redux';

type InviteToProfileProps = {
  persona: User | undefined;
  toggleSheet: () => void;
};

type ShareOption = {
  img: React.FC<React.SVGProps<any>>;
  label: string;
  onClick: () => void;
  installed: () => boolean | Promise<boolean>;
  enabled?: boolean;
};

type TrackAction = Social | `Share via...` | `friend` | `copy link`;

const InviteToProfile: React.FC<InviteToProfileProps> = ({ persona, toggleSheet }) => {
  const inviteUrl = `https://app.mymesh.io/profile/invite/${persona?.id}`;
  const { height } = useWindowDimensions();
  const navigation = useNavigation();
  const dispatch = useDispatch();

  const [searchTxt, setSearchTxt] = useState(``);
  const [options, setOptions] = useState<any>([]);
  const [shouldFetchMore, setShouldFetchMore] = useState(true);

  const carouselScroll = useRef<any>(null);

  const { data, fetchMore, loading } = useQuery<any>(SEARCH_PERSONAS, {
    fetchPolicy: `cache-and-network`,
    variables: {
      query: searchTxt,
      limit: 15,
      offset: 0,
      isVerfied: searchTxt?.length ? undefined : true,
      isFollowing: searchTxt?.length ? undefined : false,
    },
  });

  const handleKeyPress = (e: NativeSyntheticEvent<TextInputChangeEventData>) => {
    const txt = e.nativeEvent.text;
    setSearchTxt(txt);
  };

  const track = (action: TrackAction, target?: string) => {
    const input = {
      community_name: `Personal Feed`,
      action: `${action}${target ? ` ${target}` : ``}`,
    };
    PendoTrackEvent(Events.SHARE_COMMUNITY, input);
  };

  const handlesearchPersonas = useCallback(() => {
    if (!searchTxt) return;
    Keyboard.dismiss();
  }, [searchTxt]);

  const handleCopyLink = async () => {
    try {
      Clipboard?.setString(`You’re invited to Personal Feed! ${inviteUrl}`);
      console.log(`copy invite link to clipboard: `, inviteUrl);
      track(`copy link`);
    } catch (err) {
      console.error(`copy invite link to clipboard failed: `, err);
    }
  };

  const handleShareVia = async () => {
    try {
      await Share.share({ message: `Join ${persona?.handle}'s personal feed on Mesh\n${inviteUrl}` });
      track(`Share via...`);
    } catch (err) {
      console.error(`share via failed: `, err);
    }
  };

  const handleSocialShare = async (SocialType: Social) => {
    try {
      if (SocialType === SocialShare.Social.INSTAGRAM) {
        const shareOptions = {
          social: SocialShare.Social.INSTAGRAM,
          url: inviteUrl,
          type: `*`,
          forceDialog: true,
        };
        await SocialShare.shareSingle(shareOptions);
      } else if (SocialType === SocialShare.Social.MESSENGER) {
        Linking.openURL(`fb-messenger://share?link=${inviteUrl}`);
      } else if (SocialType === SocialShare.Social.SMS && Platform.OS === `ios` && inviteUrl) {
        const text = encodeURIComponent(inviteUrl);
        Linking.openURL(`sms:${Platform.OS === `ios` ? `&` : `?`}body=${text}`);
      } else if (SocialType === SocialShare.Social.EMAIL) {
        const shareOptions: ShareSingleOptions = {
          subject: `Invitation to join ${persona?.handle}'s personal feed on mesh`,
          message: `I'd like to invite you to join ${persona?.handle}'s personal feed on mesh. Tap the link to download the app and join community`,
          url: inviteUrl,
          social: SocialType,
        };
        await SocialShare.shareSingle(shareOptions);
      } else if (SocialType !== SocialShare.Social.FACEBOOK_STORIES) {
        const shareOptions: ShareSingleOptions = {
          title: `Invitation to join ${persona?.handle}'s personal feed on mesh`,
          message: `I'd like to invite you to join ${persona?.handle}'s personal feed on mesh. Tap the link to download the app and join community`,
          url: inviteUrl,
          social: SocialType,
        };
        await SocialShare.shareSingle(shareOptions);
      }
      track(SocialType);
    } catch (err) {
      console.error(`share via messages failed: `, err);
    }
  };

  const fetchMoreFriends = useCallback(() => {
    if (loading) return;
    const friendsLength = data?.searchForUsers.length;
    const variables = {
      query: searchTxt,
      offset: friendsLength,
      limit: 15,
      isVerfied: true,
      isFollowing: false,
    };

    if (!loading && shouldFetchMore)
      fetchMore({
        variables,
        updateQuery: (prev, { fetchMoreResult }) => {
          if (
            !fetchMoreResult ||
            !fetchMoreResult?.searchForUsers?.length ||
            fetchMoreResult?.searchForUsers?.slice(-1)?.[0]?.id === prev?.searchForUsers?.slice(-1)?.[0]?.id
          ) {
            setShouldFetchMore(false);
            console.log(`same result`);
            return prev;
          }
          return {
            ...prev,
            searchForUsers: unionBy(prev.searchForUsers, fetchMoreResult.searchForUsers, (li: any) => li.id),
          };
        },
      });
  }, [data?.searchForUsers, fetchMore, searchTxt, persona, loading, shouldFetchMore, setShouldFetchMore]);

  const inviteOptions: ShareOption[] = [
    {
      img: ShareViaIcon,
      label: `Share via...`,
      onClick: handleShareVia,
      installed: () => true,
    },
    {
      img: CopyLinkIcon,
      label: `Copy link`,
      onClick: handleCopyLink,
      installed: () => true,
    },
    {
      img: Platform.OS === `ios` ? MessageIcon : GoogleMessageIcon,
      label: `Messages`,
      onClick: () => handleSocialShare(SocialShare.Social.SMS),
      installed: () => true,
    },
    {
      img: WhatsAppIcon,
      label: `WhatsApp`,
      onClick: () => handleSocialShare(SocialShare.Social.WHATSAPP),
      installed: () => Linking.canOpenURL(`whatsapp://`),
    },
    {
      img: EmailIcon,
      label: `Email`,
      onClick: () => handleSocialShare(SocialShare.Social.EMAIL),
      installed: () => true,
    },
    {
      img: MessengerIcon,
      label: `Messenger`,
      onClick: () => handleSocialShare(SocialShare.Social.MESSENGER),
      installed: () => Linking.canOpenURL(`fb-messenger://`),
    },
    {
      img: InstagramIcon,
      label: `Instagram`,
      onClick: () => handleSocialShare(SocialShare.Social.INSTAGRAM),
      installed: () => (Platform.OS === `android` ? Linking.canOpenURL(`instagram://`) : false),
    },
    {
      img: TelegramIcon,
      label: `Telegram`,
      onClick: () => handleSocialShare(SocialShare.Social.TELEGRAM),
      installed: () => Linking.canOpenURL(`telegram://`),
    },
    {
      img: TwitterIcon,
      label: `Twitter`,
      onClick: () => handleSocialShare(SocialShare.Social.TWITTER),
      installed: () => Linking.canOpenURL(`twitter://`),
    },
  ];
  useEffect(() => {
    const asyncOptions = async () => {
      const optionPromise = inviteOptions.map(async (op) => {
        const enabled = await op.installed();
        return { ...op, enabled };
      });
      const data = await Promise.all(optionPromise);
      setOptions(data.filter((it) => it.enabled));
    };
    asyncOptions();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const renderItem: ListRenderItem<ShareOption> = ({ item }) => {
    const { img: ImageIcon, label, onClick } = item;
    return (
      <TouchableOpacity onPress={onClick}>
        <View style={styles.shareItemWrapper}>
          <View style={styles.manualShare}>
            <ImageIcon width={70} height={70} />
          </View>
          <Text style={styles.shareTxt}>{label}</Text>
        </View>
      </TouchableOpacity>
    );
  };

  const handleFollowUser = useCallback(
    async (isFollowing: boolean, id: string) => {
      const variables = {
        query: searchTxt,
        limit: 15,
        offset: 0,
        isVerfied: searchTxt?.length ? undefined : true,
        isFollowing: searchTxt?.length ? undefined : false,
      };
      const mutation = isFollowing ? UNFOLLOW_USER : FOLLOW_USER;
      const { data: followResult } = await client.mutate({
        mutation,
        variables: {
          userId: id,
        },
      });
      const index = data?.searchForUsers?.findIndex((n: User) => n?.id === id);
      const resKey = isFollowing ? `unFollowUser` : `followUser`;
      if (index > -1) {
        if (followResult?.[resKey]?.statusCode === 200) {
          let updatedData = {};
          let newList = [];
          if (isFollowing) {
            updatedData = { ...data?.searchForUsers[index], follow_request: null };
            newList = [...data?.searchForUsers?.slice(0, index), updatedData, ...data?.searchForUsers?.slice(index + 1)];
          } else {
            const isAccepted = followResult?.followUser?.data?.isFollowing;
            updatedData = {
              ...data?.searchForUsers[index],
              follow_request: {
                ...followResult?.followUser?.data,
                accepted: isAccepted,
              },
            };
            newList = [...data?.searchForUsers?.slice(0, index), updatedData, ...data?.searchForUsers?.slice(index + 1)];
          }
          client.writeQuery({
            query: SEARCH_PERSONAS,
            variables,
            data: {
              searchForUsers: newList,
            },
          });
        }
      }
      dispatch(setPersonalFeedRefresh(true));
    },
    [searchTxt, data, dispatch],
  );

  const navigateToProfile = useCallback(
    (user: User) => {
      toggleSheet();
      setTimeout(() => {
        navigation.navigate(`Profile`, { user, isSameStack: true });
      }, 500);
    },
    [navigation, toggleSheet],
  );

  const renderFriend: ListRenderItem<User> = ({ item }) => {
    const { isVerified, follow_request, id } = item || {};
    let followText = `Follow`;
    if (follow_request) followText = follow_request?.accepted ? `Following` : `Requested`;

    return (
      <View style={styles.friendItem}>
        <Avatar
          border
          user={item}
          size={40}
          onPress={() => {
            navigateToProfile(item);
          }}
        />
        <View style={{ flex: 1, marginLeft: 10 }}>
          <TouchableOpacity
            onPress={() => navigateToProfile(item)}
            style={{ display: `flex`, alignItems: `flex-start`, justifyContent: `space-around`, flex: 1 }}
          >
            <View style={{ flexDirection: `row`, alignItems: `center` }}>
              <Text style={Typography.text(`bold`)}>{item.handle}</Text>
              {isVerified && <MeshVerifiedCheck height={12} width={12} style={{ marginLeft: 5 }} />}
            </View>
            <Text style={styles.friendPronouns as any}>{item?.name || item?.handle}</Text>
          </TouchableOpacity>
        </View>
        {persona?.id !== id && (
          <ThemedButton
            rounded
            title={followText}
            titleStyle={{ fontSize: Typography.baseFontSize }}
            onPress={() => handleFollowUser(!!follow_request, item?.id)}
            buttonStyle={{ paddingVertical: 6, paddingHorizontal: 12 }}
            disabled={follow_request && !follow_request?.accepted}
          />
        )}
      </View>
    );
  };

  return (
    <View>
      <View style={styles.shareWrapper}>
        <FlatList<ShareOption>
          horizontal
          ref={carouselScroll}
          bounces={false}
          onScrollToIndexFailed={() => carouselScroll.current?.scrollToIndex({ index: 0, viewPosition: 0 })}
          getItemLayout={(_, index) => ({ length: 72, offset: 72 * index, index })}
          showsVerticalScrollIndicator={false}
          showsHorizontalScrollIndicator={false}
          initialNumToRender={10}
          maxToRenderPerBatch={12}
          data={options}
          contentContainerStyle={{ alignItems: `center` }}
          renderItem={renderItem}
          keyExtractor={(item: any) => item.label}
        />
      </View>

      <View style={{ paddingHorizontal: 20, alignItems: `center` }}>
        <Text style={{ paddingVertical: 5 }}>Mesh is better with friends!</Text>
        <View style={styles.inputWrapper}>
          <TextInput
            style={styles.textInput}
            onChange={handleKeyPress}
            onEndEditing={handlesearchPersonas}
            placeholder="Search friends on Mesh"
          >
            {searchTxt}
          </TextInput>

          <TouchableOpacity
            hitSlop={{ top: 15, left: 15, right: 15, bottom: 15 }}
            style={{ padding: 10 }}
            onPress={handlesearchPersonas}
          >
            <MeshIcon name="magnifyingglass" size={20} color={Colors.mediumGray} />
          </TouchableOpacity>
        </View>
      </View>

      <View style={[styles.friendsList, { height: data?.searchForUsers?.length > 9 ? height * 0.5 : 'auto' }]}>
        <FlatList<User>
          data={data?.searchForUsers || []}
          ListFooterComponent={() => (loading ? <LoadingIndicator /> : <></>)}
          keyExtractor={(item, index) => `${item.id}-${index}`}
          ListHeaderComponent={() => (
            <Text style={{ paddingHorizontal: 20, paddingVertical: 5, color: Colors.gray }}>Suggested</Text>
          )}
          renderItem={renderFriend}
          onEndReachedThreshold={0.3}
          onEndReached={fetchMoreFriends}
        />
      </View>
    </View>
  );
};

const styles = StyleSheet.create({
  shareWrapper: {
    display: `flex`,
    flexDirection: `row`,
    marginBottom: 15,
    paddingLeft: 20,
  },
  shareItemWrapper: {
    display: `flex`,
    flexDirection: `column`,
    alignItems: `center`,
  },
  manualShare: {
    borderColor: Colors.mediumLightGray,
    display: `flex`,
    justifyContent: `center`,
    alignItems: `center`,
    overflow: `hidden`,
  },
  shareTxt: {
    fontSize: 12,
    color: Colors.darkGray,
  },
  inputWrapper: {
    borderWidth: 1,
    borderColor: `#D8D8D8`,
    borderRadius: 4,
    display: `flex`,
    flexDirection: `row`,
    justifyContent: `space-between`,
    alignItems: `center`,
    width: `100%`,
  },
  textInput: {
    flex: 1,
    paddingHorizontal: 10,
  },
  friendsList: {
    minHeight: 150,
    maxHeight: `100%`,
    paddingTop: 10,
  },
  friendItem: {
    display: `flex`,
    flexDirection: `row`,
    alignItems: `center`,
    paddingVertical: 4,
    paddingHorizontal: 20,
  },
  friendPronouns: {
    ...Typography.text(`small`, `gray`, `bold`),
  },
});

export default InviteToProfile;
